import { Table, TableColumnsType } from 'antd'
import React from 'react'
import { Box, Grid, IconButton, Typography } from '@mui/material'
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail'
import InfoIcon from '@mui/icons-material/Info'
import dayjs from 'dayjs'

import { useUser } from '../../../../../../../hooks/useUser'
import {
  EOrderStatus,
  IOrder,
  IOrderItem,
  OrderRecipient,
  OrderShippingProvider,
} from '../../../../../../../types/order'
import CircularLoading from '../../../../../../../components/CircularProgress'
import TrackingProviderImage from '../../../../../../../components/TrackingProviderImage'
import {
  ONLY_DATE_FORMAT,
  ONLY_TIME_FORMAT,
} from '../../../../../../../constants/global'
import { Link, useNavigate } from 'react-router-dom'
import PageUrls from '../../../../../../../constants/page-urls'
import ImgTag from '../../../../../../../components/ImgTag'
import ProductItemDetailTag from '../../../../../../../components/ProductItemDetailTag'
import OrderTag from '../../../../../../../components/OrderTag'
import Pagination from '../../../../../../../components/Pagination'
import { IFilterMapped } from './LazadaOrderFilter'
import { Tag } from '../../../../../../../components/Tag'

interface IOrderTableProps {
  selectedData: IOrder[]
  setSelectedData: (orders: IOrder[]) => void
  isLoading: boolean
  data: IOrder[]
  filter: IFilterMapped
  onFilterChange: (filter: IFilterMapped) => void
  total: number
}

enum OrderStatusColor {
  'pending' = 'warning.main',
  'telesale' = 'secondary.main',
  'salepage' = 'text.secondary',
  'success' = 'success.main',
  'cancelled' = 'error.main',
}

const OrderListTable = ({
  selectedData,
  setSelectedData,
  isLoading,
  data,
  filter,
  onFilterChange,
  total,
}: IOrderTableProps) => {
  const { merchant } = useUser()
  const showSocial = !!merchant?.config?.display?.social

  const navigate = useNavigate()

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    const keys = newSelectedRowKeys as string[]
    const orderObjects = data?.reduce<{ [key: string]: IOrder }>((acc, cur) => {
      acc[cur.objectId] = cur
      return acc
    }, {})
    const orders = keys.map((key: string) => {
      return orderObjects[key]
    })
    setSelectedData(orders)
  }

  const columns: TableColumnsType<IOrder & { key: string; numberRow: number }> =
    [
      {
        title: 'ลำดับ',
        dataIndex: 'numberRow',
        key: 'numberRow',
        align: 'center' as const,
        width: 80,
        render: (numberRow: number) => {
          return (
            <Box>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                textAlign={'center'}
              >
                {numberRow}
              </Typography>
            </Box>
          )
        },
      },
      {
        title: 'สถานะ',
        dataIndex: 'status',
        key: 'status',
        align: 'center' as const,
        render: (status: string, order: IOrder) => {
          return (
            <>
              {order.trackingCode && order.trackingProvider && (
                <Box mb={1}>
                  <TrackingProviderImage
                    trackingProvider={
                      order.trackingProvider as OrderShippingProvider
                    }
                  />
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    textAlign={'center'}
                  >
                    {order.trackingCode}
                  </Typography>
                </Box>
              )}
              <OrderTag order={order} status={status as EOrderStatus} />
            </>
          )
        },
      },
      {
        title: 'เลขออเดอร์',
        dataIndex: 'code',
        key: 'code',
        align: 'center' as const,
        render: (code: string, order: IOrder) => (
          <>
            <Typography
              variant="subtitle1"
              color="text.primary"
              component={Link}
              to={`${PageUrls.Orders.Detail}/${order.objectId}`}
              sx={{ cursor: 'pointer' }}
            >
              {code}
            </Typography>
          </>
        ),
      },
      {
        title: 'ราคา',
        dataIndex: 'total',
        key: 'total',
        align: 'center' as const,
        render: (total: string, order: IOrder) => (
          <Grid
            container
            flexDirection={'column'}
            textAlign={'center'}
            minWidth={120}
          >
            <Grid item>
              <Typography variant="h5" color="error.main">
                {total.toLocaleString()}
              </Typography>
            </Grid>
            <Grid item>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ marginTop: 1 }}
              >
                {!order?.isCod ? 'เก็บเงินปลายทาง' : 'โอนผ่านธนาคาร'}
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="subtitle2" color="text.secondary">
                {order.items.length} รายการ/{order.quantity} ชิ้น
              </Typography>
            </Grid>
          </Grid>
        ),
      },
      {
        title: 'จัดส่ง',
        dataIndex: 'recipient',
        key: 'recipient',
        align: 'center' as const,
        render: (recipient: OrderRecipient) => (
          <Grid
            container
            flexDirection={'column'}
            minWidth={200}
            textAlign={'left'}
          >
            <Grid item>
              <Typography variant="subtitle1">{recipient?.name}</Typography>
            </Grid>
            <Grid item>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ marginTop: 1 }}
              >
                {recipient?.address} {recipient?.subDistrict}{' '}
                {recipient?.district} {recipient?.province} {recipient?.zipcode}
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="subtitle1">{recipient?.phone}</Typography>
            </Grid>
            {showSocial && recipient?.social && (
              <Grid item>
                <Box
                  display="flex"
                  alignItems="center"
                  gap={0.5}
                  title="โซเชียลของลูกค้า"
                >
                  <AlternateEmailIcon
                    sx={{ fontSize: 14 }}
                    color="disabled"
                  />
                  <Typography variant="subtitle2" color="text.secondary">
                    {recipient.social}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        ),
      },
      {
        title: 'สินค้า',
        dataIndex: 'items',
        key: 'items',
        align: 'center' as const,
        render: (items: IOrderItem[]) => (
          <Box minWidth={150}>
            {items?.map((item: IOrderItem, index: number) => {
              return (
                <ProductItemDetailTag
                  key={index}
                  item={{
                    name: item.productName,
                    quantity: item.quantity,
                    url: item.imageUrl,
                    color: item.meta?.color,
                    size: item.meta?.size,
                  }}
                />
              )
            })}
          </Box>
        ),
      },
      {
        title: 'หมายเหตุ',
        dataIndex: 'note',
        key: 'note',
        align: 'center' as const,
        render: (note: string, order) => (
          <>
            <Tag
              color={order?.printedAt ? 'success' : 'error'}
              variant="outlined"
              sx={{ marginBottom: 1 }}
            >
              {order?.printedAt ? 'ปริ้นแล้ว' : 'ยังไม่ปริ้น'}
            </Tag>
            <Typography variant="subtitle2" textAlign={'center'}>
              {note?.length > 0 ? note : <em>ไม่ระบุ</em>}
            </Typography>
          </>
        ),
      },
      {
        title: 'วันที่เพิ่ม',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 120,
        align: 'center' as const,
        render: (createdAt: string, order: IOrder) => (
          <Grid container flexDirection={'column'} textAlign={'center'}>
            <Grid item>
              <Typography variant="subtitle1">{order?.actor}</Typography>
            </Grid>
            <Grid item>
              <Typography variant="subtitle2" color="text.secondary">
                {dayjs(createdAt).format(ONLY_DATE_FORMAT)}
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="caption" color="text.secondary">
                {dayjs(createdAt).format(ONLY_TIME_FORMAT)}
              </Typography>
            </Grid>
          </Grid>
        ),
      },
      // {
      //   dataIndex: '',
      //   key: 'action',
      //   width: 60,
      //   align: 'center' as const,
      //   render: (_: undefined, order: IOrder) => <Grid container spacing={2} justifyContent="flex-end">
      //     <Grid item>
      //       <IconButton
      //         onClick={() => {
      //           navigate(`${PageUrls.Orders.Detail}/${order.objectId}`, {
      //             replace: true,
      //           })
      //         }}
      //       >
      //         <InfoIcon />
      //       </IconButton>
      //     </Grid>
      //   </Grid>,
      // }
    ]

  const rowSelection = {
    selectedRowKeys: selectedData.map((order: IOrder) => order.objectId),
    onChange: onSelectChange,
  }

  return (
    <>
      <CircularLoading isLoading={isLoading} />
      <Table
        size={'middle'}
        rowSelection={rowSelection}
        columns={columns}
        scroll={{
          x: columns.reduce(
            (pre, cur: any) => (cur.width ? cur.width + pre : pre),
            0,
          ),
        }}
        dataSource={data?.map((order: IOrder, index: number) => {
          return {
            ...order,
            key: order.objectId,
            numberRow:
              index + 1 + ((filter.page || 0) - 1) * (filter.perPage || 0),
          }
        })}
        bordered
        pagination={false}
      />
      <Pagination
        total={total}
        current={filter.page!}
        pageSize={filter.perPage!}
        onChange={(page, perPage) =>
          onFilterChange({ ...filter, page, perPage })
        }
      />
    </>
  )
}

export default OrderListTable
